import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { ContentfulEntry, ContentfulProjectFields } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { getEntries } from '../lib/contentful';

const normalizeBlockquoteQuotes = (markdown: string): string => {
  return markdown.replace(
    /(^>\s*)(["'“”‘’]{1,2})([^\n]*?)(["'“”‘’]{1,2})\s*$/gm,
    (_, prefix, _openQuote, quoteText, _closeQuote) => `${prefix}${quoteText.trim()}`,
  );
};

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string}>();
  const [project, setProject] = useState<ContentfulEntry<ContentfulProjectFields> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const entries = await getEntries('project');
        const foundProject = entries.find((entry) => entry.fields.slug === slug);
        setProject(foundProject as ContentfulEntry<ContentfulProjectFields> || null);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
        <p className="text-gray-600 mb-8">
          The project you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/projects"
          className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-4xl mx-auto">
      {/* Back to Projects Link */}
      <Link
        to="/projects"
        className="inline-flex items-center text-green-600 hover:text-green-700 font-medium mb-8"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Projects
      </Link>

      {/* Banner/Hero Image - prioritize bannerImage, then heroImage, then featuredImage */}
      {(project.fields.bannerImage?.[0] || project.fields.heroImage?.[0] || project.fields.featuredImage) && (
        <div className="w-full h-96 mb-8 rounded-lg overflow-hidden">
          <img
            src={
              project.fields.bannerImage?.[0]?.secure_url ||
              project.fields.heroImage?.[0]?.secure_url ||
              (project.fields.featuredImage?.fields.file.url.startsWith('//') 
                ? `https:${project.fields.featuredImage.fields.file.url}`
                : project.fields.featuredImage?.fields.file.url)
            }
            alt={project.fields.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Title */}
      <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
        {project.fields.title}
      </h1>

      {/* Technologies */}
      {project.fields.technologies && project.fields.technologies.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-gray-200">
          {project.fields.technologies.map((tech, index) => (
            <span
              key={index}
              className="text-xs font-semibold uppercase tracking-wide text-green-600 bg-green-50 px-3 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>
      )}

      {/* Description */}
      <div className="text-xl text-gray-700 leading-relaxed mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-green-600">
        {project.fields.description}
      </div>

      {/* Content */}
      {project.fields.content && (
        <div className="prose prose-lg max-w-none mb-12">
          {(() => {
            const contentData = project.fields.content;
            
            if (!contentData) {
              return null;
            }
            
            // If it's a string, render as Markdown
            if (typeof contentData === 'string') {
              const normalizedMarkdown = normalizeBlockquoteQuotes(contentData);

              return (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Fix protocol-relative URLs for images
                    img: ({ node, ...props }) => (
                      <img 
                        {...props} 
                        src={props.src?.startsWith('//') ? `https:${props.src}` : props.src}
                        loading="lazy"
                        className="rounded-lg"
                      />
                    ),
                    // Style links
                    a: ({ node, ...props }) => (
                      <a {...props} className="text-green-600 hover:text-green-700" />
                    ),
                    // Ensure headings have proper spacing
                    h1: ({ node, ...props }) => (
                      <h1 {...props} className="text-4xl font-bold mt-8 mb-4 text-gray-900" />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 {...props} className="text-3xl font-bold mt-8 mb-4 text-gray-900" />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 {...props} className="text-2xl font-bold mt-6 mb-3 text-gray-900" />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4 {...props} className="text-xl font-bold mt-6 mb-3 text-gray-900" />
                    ),
                    h5: ({ node, ...props }) => (
                      <h5 {...props} className="text-lg font-bold mt-4 mb-2 text-gray-900" />
                    ),
                    h6: ({ node, ...props }) => (
                      <h6 {...props} className="text-base font-bold mt-4 mb-2 text-gray-900" />
                    ),
                    // Style paragraphs
                    p: ({ node, ...props }) => (
                      <p {...props} className="mb-4 leading-relaxed text-gray-700" />
                    ),
                    // Style lists
                    ul: ({ node, ...props }) => (
                      <ul {...props} className="list-disc list-inside mb-4 space-y-2 text-gray-700" />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol {...props} className="list-decimal list-inside mb-4 space-y-2 text-gray-700" />
                    ),
                    li: ({ node, ...props }) => (
                      <li {...props} className="ml-4" />
                    ),
                    // Style blockquotes
                    blockquote: ({ node, ...props }) => (
                      <blockquote {...props} className="border-l-4 border-green-600 pl-4 italic my-4 text-gray-600" />
                    ),
                    // Style tables
                    table: ({ node, ...props }) => (
                      <div className="my-6 w-full overflow-x-auto">
                        <table {...props} className="min-w-[640px] w-full border-collapse border border-gray-300" />
                      </div>
                    ),
                    thead: ({ node, ...props }) => (
                      <thead {...props} className="bg-gray-50" />
                    ),
                    tbody: ({ node, ...props }) => (
                      <tbody {...props} className="bg-white" />
                    ),
                    tr: ({ node, ...props }) => (
                      <tr {...props} className="border-b border-gray-200" />
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        {...props}
                        className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-900"
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td {...props} className="border border-gray-300 px-4 py-2 text-sm text-gray-700" />
                    ),
                    // Style code blocks
                    code: ({ node, inline, ...props }: any) => 
                      inline ? (
                        <code {...props} className="bg-gray-100 text-pink-600 px-2 py-1 rounded text-sm font-mono" />
                      ) : (
                        <code {...props} className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 text-sm font-mono" />
                      ),
                    pre: ({ node, ...props }) => (
                      <pre {...props} className="my-4" />
                    ),
                  }}
                >
                  {normalizedMarkdown}
                </ReactMarkdown>
              );
            }
            
            // If it's a rich text document, use the renderer
            return documentToReactComponents(contentData as Document);
          })()}
        </div>
      )}

      {/* Gallery */}
      {project.fields.gallery && project.fields.gallery.length > 0 && (
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Gallery</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.fields.gallery.map((image, index) => (
              <div key={index} className="rounded-lg overflow-hidden shadow-md">
                <img
                  src={image.fields.file.url}
                  alt={image.fields.title || `Gallery image ${index + 1}`}
                  className="w-full h-64 object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demo Site Link */}
      {project.fields.demoSiteUrl && (
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Visit Project</h3>
          <a
            href={project.fields.demoSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            View Live Project
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      )}

      {/* Related Projects */}
      <div className="mt-16 pt-12 border-t border-gray-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-8">More Projects</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/projects" className="group">
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
                Explore more projects
              </h4>
              <p className="text-gray-600 text-sm">
                Browse our complete collection of projects and creations
              </p>
            </div>
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ProjectDetail;
